/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Collapse, CollapseProps } from 'antd-v5';
import { RootState } from 'app/AppStore';
import { SearchNoResultIcon } from 'assets/icons';
import Loader from 'components/Loader';
import { getExadisEquivalentsForRdVehiclesEnabled } from 'domains/appContext/AppContext.store';
import { createIAMSupplierReferenceKey, IAMFulltextSearchResult } from 'domains/catalog/Catalog.types';
import { getIAMReferences, getPricesMap, getStocksMapV2 } from 'domains/references';
import { SparePartsViewType } from 'domains/user';

import { CompareTableState } from 'pages/CatalogPage/IAM/FullTextSearchResult/CompareTable';
import { CompareTableModal } from 'pages/CatalogPage/IAM/FullTextSearchResult/CompareTableModal';
import { IamProductCard } from 'pages/CatalogPage/IAM/FullTextSearchResult/IAMProductCard';
import { useWindowWidth } from 'pages/CatalogPage/IAM/FullTextSearchResult/utils';
import BamCommercialFamilyLogo from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/BamCommercialFamilyLogo/BamCommercialFamilyLogo';
import IAMPlateReferenceCard from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/PlateReferenceCard';
import {
  getFilteredMotrioReferences,
  getFilteredOtherBrandsReferences,
  sortIAMReferences,
} from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCardsContainerUtils';
import { theme } from 'styles';
import { Box, CenteredSpin, Flex, Icon, LeftButton, MarginBox, RightButton, Text } from 'UI';
import { getData, isLoading } from 'utils';

interface SearchResultSectionProps {
  searchResultGroup: IAMFulltextSearchResult;
  sparePartsView: SparePartsViewType;
}

interface EquivalentSectionProps {
  sparePartsView: SparePartsViewType;
  equivalentReferences: string[] | undefined;
  additionalEquivalencesLoading: boolean;
  handleClickCompare?: (reference: string) => void;
  hasMainReference?: boolean;
  vehicleKey?: string;
  hideMotrio?: boolean;
}

const SearchResultSection = ({ searchResultGroup, sparePartsView }: SearchResultSectionProps) => {
  const location = useLocation();
  const isExadisEnabled = useSelector(getExadisEquivalentsForRdVehiclesEnabled);
  const hideMotrio = isExadisEnabled && new URLSearchParams(location.search).get('hideMotrio') === 'true';
  const equivalentReferences = getData(searchResultGroup.equivalentReferences)?.map((r) => r.referenceNumber);
  const mainReferenceNumber = createIAMSupplierReferenceKey(
    searchResultGroup.mainReferences?.[0]?.referenceNumber ?? '',
    searchResultGroup.mainReferences?.[0]?.supplierCode,
  );
  const compareTableInitialState = {
    firstReference: mainReferenceNumber ?? '',
    secondReference: '',
    thirdReference: '',
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [compareTableReferences, setCompareTableReferences] = useState<CompareTableState>(compareTableInitialState);

  const handleClickCompare = (reference: string) => {
    setIsModalOpen(true);
    setCompareTableReferences({ ...compareTableReferences, secondReference: reference });
  };

  const handleModalOnCancel = () => {
    setIsModalOpen(false);
    setCompareTableReferences(compareTableInitialState);
  };

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <>
          <Text type="heading3">{`${searchResultGroup.groupName?.toUpperCase()}`}</Text>
          <MainSection searchResultGroup={searchResultGroup} sparePartsView={sparePartsView} />
        </>
      ),
      children: (
        <div data-cy={'equivalent-products-section'}>
          <EquivalentSection
            sparePartsView={sparePartsView}
            equivalentReferences={equivalentReferences}
            additionalEquivalencesLoading={searchResultGroup.additionalEquivalencesLoading}
            handleClickCompare={handleClickCompare}
            hasMainReference={!!mainReferenceNumber}
            hideMotrio={hideMotrio}
          />
        </div>
      ),
    },
  ];

  return (
    <Box>
      {mainReferenceNumber && (
        <BamCommercialFamilyLogo
          referenceNumber={mainReferenceNumber}
          iconSize={50}
          boxWidth={100}
          boxHeight={60}
          boxOffsetBottom={-25}
          boxOffsetRight={10}
        />
      )}
      <Collapse
        items={items}
        defaultActiveKey={['1']}
        expandIconPosition={'end'}
        collapsible={'icon'}
        style={{
          background: theme.color.white,
          borderColor: theme.color.white,
          padding: '15px 15px',
          margin: '10px',
          borderRadius: '8px',
          boxShadow: `0px 4px 10px 0px ${theme.color.grey75}`,
          position: 'relative',
        }}
        bordered={true}
      />
      <CompareTableModal
        open={isModalOpen}
        compareTableReferences={compareTableReferences}
        setCompareTableReferences={setCompareTableReferences}
        onCancel={handleModalOnCancel}
        compareReferencesList={equivalentReferences}
      />
    </Box>
  );
};

const MainSection = ({ searchResultGroup, sparePartsView }: SearchResultSectionProps) => {
  const { t } = useTranslation();

  const hasRefs = (searchResultGroup.mainReferences?.length ?? 0) > 0;

  const renderNoMainProduct = useMemo(
    () => (
      <Flex direction="column" justify="center" align="center">
        <Icon IconComponent={SearchNoResultIcon} size={80} />
        <Box height={12} />
        <Text type="heading3">
          {t('catalog.search.iam.main_products.search.no_result.title', 'References currently unavailable.')}
        </Text>
        <Text type="h3_dim">
          {t(
            'catalog.search.iam.main_products.search.no_result.description',
            'We have listed our best fit Exadis catalog',
          )}
        </Text>
      </Flex>
    ),
    [t],
  );

  const renderGroupMainSection = useMemo(() => {
    if (!hasRefs) {
      return renderNoMainProduct;
    }
    return searchResultGroup.mainReferences?.map((mainRef) => (
      <IAMPlateReferenceCard
        key={mainRef.referenceNumber}
        referenceNumber={mainRef.referenceNumber}
        sparePartsView={sparePartsView}
        bordered={false}
        isIamSearchResult
        supplierCode={mainRef.supplierCode}
      />
    ));
  }, [hasRefs, renderNoMainProduct, searchResultGroup.mainReferences, sparePartsView]);

  return (
    <MarginBox mx={15} my={15} data-cy={'iam-reference-display'}>
      {searchResultGroup.loading ? <Loader height="10vh" size="default" /> : renderGroupMainSection}
    </MarginBox>
  );
};

export const EquivalentSection = ({
  sparePartsView,
  equivalentReferences,
  additionalEquivalencesLoading,
  handleClickCompare,
  hasMainReference,
  vehicleKey,
  hideMotrio = false,
}: EquivalentSectionProps) => {
  const { t } = useTranslation();
  const references = useSelector((state: RootState) =>
    getIAMReferences(state, { vehicleKey: undefined, referenceNumbers: equivalentReferences }),
  );
  const prices = useSelector((state: RootState) => getPricesMap(state, equivalentReferences ?? []));
  const stocks = useSelector((state: RootState) => getStocksMapV2(state, equivalentReferences ?? []));
  const hasReferences = equivalentReferences && equivalentReferences.length > 0;
  const sortedReferences = useMemo(() => {
    const otherBrandsReferences = sortIAMReferences(getFilteredOtherBrandsReferences(references), stocks);
    if (hideMotrio) {
      return otherBrandsReferences.map((r) => r.referenceNumber);
    }
    const motrioReferences = sortIAMReferences(getFilteredMotrioReferences(references, prices, stocks), stocks);
    return [...motrioReferences, ...otherBrandsReferences].map((r) => r.referenceNumber);
  }, [prices, references, stocks, hideMotrio]);

  const isAnyReferenceLoading = references.some((r) => isLoading(r.detailStatus));

  const containerRef = useRef<HTMLDivElement>() as React.MutableRefObject<HTMLInputElement>;
  const itemWidth = 332;
  const defaultDisplayedCardCount = 3;
  const displayedCardsCount = containerRef.current
    ? Math.floor(containerRef.current.clientWidth / itemWidth)
    : defaultDisplayedCardCount;
  const scrollStepSize = (displayedCardsCount > 1 ? displayedCardsCount - 1 : 1) * itemWidth;

  const [isLeftDisabled, setIsLeftDisabled] = useState(true);
  const [isRightDisabled, setIsRightDisabled] = useState(false);

  const updateButtonStates = (scrollLeft: number) => {
    if (containerRef.current) {
      const { scrollWidth, clientWidth } = containerRef.current;
      setIsLeftDisabled(scrollLeft <= 0);
      setIsRightDisabled(scrollLeft + clientWidth >= scrollWidth);
    }
  };
  const windowWidth = useWindowWidth();

  useEffect(() => {
    if (containerRef.current) {
      updateButtonStates(containerRef.current.scrollLeft);
    }
  }, [windowWidth]);

  const renderSectionPlate = useMemo(() => {
    const handleLeftButtonClick = () => {
      if (containerRef.current) {
        const { scrollLeft } = containerRef.current;
        const scrollAmount = scrollLeft - scrollStepSize;
        containerRef.current.scrollTo({
          left: scrollAmount,
          behavior: 'smooth',
        });
        updateButtonStates(scrollAmount);
      }
    };

    const handleRightButtonClick = () => {
      if (containerRef.current) {
        const { scrollLeft } = containerRef.current;
        const scrollAmount = scrollLeft + scrollStepSize;
        containerRef.current.scrollTo({
          left: scrollAmount,
          behavior: 'smooth',
        });
        updateButtonStates(scrollAmount);
      }
    };

    return (
      <MarginBox mx={15}>
        <Flex direction={'row'} justify={'space-between'} gap={48}>
          <Flex direction={'column'}>
            <Flex direction={'row'}>
              <Text type="heading4" color="clear_blue">
                {t('catalog.search.iam.equivalent_products.title', 'Equivalent products')}
              </Text>
              <Box width={10} />
              <Text type={'h5_light'}>
                {t('catalog.search.iam.equivalent_products.products_count', '({{count}} products)', {
                  count: equivalentReferences?.length,
                })}
              </Text>
            </Flex>
            <Box height={4} />
            <Text type="lg">
              {t(
                'catalog.search.iam.equivalent_products.description',
                'All listed products have the same or substantially the same external appearance, and performance characteristics as manufacturer parts.',
              )}
            </Text>
          </Flex>
          {hasReferences && !(isLeftDisabled && isRightDisabled) && (
            <Flex size={0} direction={'row'} justify={'flex-end'} gap={6}>
              <LeftButton disabled={isLeftDisabled} onClick={handleLeftButtonClick} />
              <RightButton disabled={isRightDisabled} onClick={handleRightButtonClick} />
            </Flex>
          )}
        </Flex>
      </MarginBox>
    );
  }, [equivalentReferences?.length, hasReferences, isLeftDisabled, isRightDisabled, scrollStepSize, t]);

  if (isAnyReferenceLoading || additionalEquivalencesLoading) {
    return <CenteredSpin />;
  }
  if (!hasReferences) {
    return renderSectionPlate;
  }

  return (
    <>
      {renderSectionPlate}
      <Box height={12} />
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          overflow: 'hidden',
          padding: '15px 15px 15px 15px',
          margin: '0px 15px',
          gap: '32px',
        }}
      >
        {sortedReferences.map((eqRef) => {
          return (
            <div key={eqRef}>
              <IamProductCard
                referenceNumber={eqRef}
                sparePartsView={sparePartsView}
                handleClickCompare={handleClickCompare}
                showCompare={hasMainReference}
                vehicleKey={vehicleKey}
              />
            </div>
          );
        })}
      </div>
      <Box height={50} />
    </>
  );
};

export default SearchResultSection;
